import useApiService from '~/composables/api/useApiService';
import { apiEndpoints } from '@/constants';
import { useAccountListStore } from '@/stores/accounts/account-list-store';
import { useIndividualFormStore } from '~/stores/accounts/add-account/individual-form-store';
import { useLegalEntityFormStore } from '~/stores/accounts/add-account/legal-entity-form-store';
import { useOnboardingLinkStore } from '~/stores/onboarding-link-store';
import type {
  AccountModel,
  AccountListModel,
  AccountOptions,
  AddAccountResponse,
  AddAccountWalletBody,
  AddProfileWithWalletBody,
  AccountListStoreOptions,
  ApiError,
  OnboardingLinkResponse,
  PendingAccountListModel,
  PendingAccountListStoreOptions
} from '~/types';
import { EntityType } from '~/enums/accounts/entity-type.enum';
import { useAddAccountStore } from '~/stores/accounts/add-account/add-account-store';
import { useAccountDetailsStore } from '~/stores/accounts/account-details-store';

export default function useAccountsApi() {
  const { get, post } = useApiService();
  const partner = localStorage.getItem('partnerName');

  const getAccountList = async (options: AccountListStoreOptions) => {
    const store = useAccountListStore();
    const params = store.createParams(options);
    const { data, error } = await get(`${apiEndpoints.accounts}?${params}`);

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    store.isDataLoaded = true;

    return data.value as AccountListModel;
  };

  const getPendingAccountsList = async (options: PendingAccountListStoreOptions) => {
    const store = useAccountListStore();
    const params = store.createParams(options);
    const { data, error } = await get(
      `${apiEndpoints.accounts}${apiEndpoints.pendingAccounts}?${params}`
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    store.isDataLoaded = true;

    return data.value as PendingAccountListModel;
  };

  const getAccountDetails = async () => {
    const route = useRoute();
    const { data, error } = await get(`${apiEndpoints.accounts}/${route.params.id}`);

    if (error.value) {
      navigateTo(`/${partner}/accounts/all`);
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as AccountModel;
  };

  const getAccountDocumentUrl = async (url: string): Promise<string | undefined> => {
    const { data, error } = await get(url);

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    if (data.value instanceof Blob) {
      return window.URL.createObjectURL(data.value);
    }
  };

  const getAccountOptions = async () => {
    const { data, error } = await get(`${apiEndpoints.accounts}/options`, 'getAccountOptions');

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as AccountOptions;
  };

  const getAccountComplianceReportDocument = async () => {
    const route = useRoute();
    const { data, error } = await get(
      `${apiEndpoints.accounts}/${route.params.id}${apiEndpoints.complianceReport}`
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    const reportBlob = new Blob([data.value as string], { type: 'text/csv' });
    return window.URL.createObjectURL(reportBlob);
  };

  const postIndividualAccount = async () => {
    const store = useIndividualFormStore();

    const { data, error } = await post(
      `${apiEndpoints.accounts}/individual`,
      store.addIndividualAccountBody
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as AddAccountResponse;
  };

  const postLegalEntityAccount = async () => {
    const store = useLegalEntityFormStore();

    const { data, error } = await post(
      `${apiEndpoints.accounts}/legal-entity`,
      store.addLegalEntityAccountBody
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as AddAccountResponse;
  };

  const postOnboardingLink = async (isAccountDetails: boolean) => {
    const { onboardingLinkBody } = useOnboardingLinkStore();
    const { entityType } = useAddAccountStore();
    const { type } = useAccountDetailsStore();
    const selectedType = isAccountDetails ? type : entityType;
    const entityPath = selectedType === EntityType.Individual ? 'individual' : 'legal-entity';

    const { data, error } = await post(
      `${apiEndpoints.accounts}/${entityPath}/onboarding`,
      onboardingLinkBody(isAccountDetails)
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as OnboardingLinkResponse;
  };

  const postOnboardingResume = async (onboardingId: string | undefined) => {
    const path = `${apiEndpoints.accounts}/Onboarding/${onboardingId}/Resume`;
    const { data, error } = await post(path, null);

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }
    return data.value as OnboardingLinkResponse;
  };

  const getAccountDocumentFile = async (url: string): Promise<ArrayBuffer> => {
    const { data, error } = await get(url, 'getAccountDocumentFile', {
      responseType: 'arrayBuffer'
    });

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value as ArrayBuffer;
  };

  const postAccountWallet = async (
    accountType: EntityType,
    accountId: string,
    profileId: string,
    body: AddAccountWalletBody
  ) => {
    const entityPath = accountType === EntityType.Individual ? 'individual' : 'legal-entity';

    const { data, error } = await post(
      `${apiEndpoints.accounts}/${entityPath}/${accountId}/profiles/${profileId}/wallets`,
      body,
      'postAccountWallet'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value;
  };

  const postProfileWithWallet = async (
    accountType: EntityType,
    accountId: string,
    body: AddProfileWithWalletBody
  ) => {
    const entityPath = accountType === EntityType.Individual ? 'individual' : 'legal-entity';

    const { data, error } = await post(
      `${apiEndpoints.accounts}/${entityPath}/${accountId}/profiles`,
      body,
      'postProfileWithWallet'
    );

    if (error.value) {
      const cause = error.value.cause as ApiError;
      throw new Error(cause.Message);
    }

    return data.value;
  };

  return {
    getAccountList,
    getPendingAccountsList,
    getAccountDetails,
    getAccountDocumentUrl,
    getAccountDocumentFile,
    getAccountOptions,
    postIndividualAccount,
    postLegalEntityAccount,
    postAccountWallet,
    postProfileWithWallet,
    postOnboardingLink,
    postOnboardingResume,
    getAccountComplianceReportDocument
  };
}
